/**
 * AccountManager - Shared account management module
 *
 * This module eliminates circular dependencies between user and accounts stores
 * by providing a centralized account management layer.
 */

import {
  addAccount,
  clearAllAccounts,
  getActiveAccount,
  getActiveAccountId,
  getStoredAccounts,
  removeAccount,
  StoredAccount,
  switchToAccount,
} from '@/utils/auth'

export class AccountManager {
  private static accountsStoreInstance: any = null

  /**
   * Register the accounts store instance for callbacks
   */
  static registerAccountsStore(store: any) {
    this.accountsStoreInstance = store
  }

  /**
   * Save a new account or update existing one
   * Handles both platform and team contexts
   */
  static async saveAccount(account: StoredAccount): Promise<void> {
    // Add account to storage
    addAccount(account)

    // If accounts store is registered, update it
    if (this.accountsStoreInstance) {
      const currentAccounts = this.accountsStoreInstance.getState().accounts
      const existingIndex = currentAccounts.findIndex((acc: StoredAccount) => acc.id === account.id)

      let updatedAccounts
      if (existingIndex >= 0) {
        updatedAccounts = [...currentAccounts]
        updatedAccounts[existingIndex] = account
      } else {
        updatedAccounts = [...currentAccounts, account]
      }

      this.accountsStoreInstance.setState({
        accounts: updatedAccounts,
        activeAccountId: account.id,
      })
    }
  }

  /**
   * Switch to a different account
   */
  static async switchAccount(accountId: number): Promise<void> {
    const accounts = this.getStoredAccounts()
    const targetAccount = accounts.find((acc) => acc.id === accountId)

    if (!targetAccount) {
      throw new Error(`Account with id ${accountId} not found`)
    }

    switchToAccount(accountId)

    // The page will reload, so no need to update store state
    window.location.reload()
  }

  /**
   * Remove an account by ID
   */
  static async removeAccountById(accountId: number): Promise<void> {
    removeAccount(accountId)

    const remainingAccounts = this.getStoredAccounts()

    if (remainingAccounts.length === 0) {
      window.location.href = '/login'
    } else {
      window.location.reload()
    }
  }

  /**
   * Clear all accounts
   */
  static clearAll(): void {
    clearAllAccounts()
    window.location.href = '/login'
  }

  /**
   * Get all stored accounts
   */
  static getStoredAccounts(): StoredAccount[] {
    return getStoredAccounts()
  }

  /**
   * Get the currently active account
   */
  static getActiveAccount(): StoredAccount | null {
    return getActiveAccount()
  }

  /**
   * Get the active account ID
   */
  static getActiveAccountId(): number | null {
    return getActiveAccountId()
  }

  /**
   * Check if should save account (team users don't support multi-account)
   */
  static shouldSaveAccount(saveAccount: boolean, scope?: number): boolean {
    // Team users (scope = 1) don't support multi-account feature
    const LoginScope = { Team: 1 }
    return saveAccount && scope !== LoginScope.Team
  }

  /**
   * Save account after successful login
   */
  static async saveAccountAfterLogin(
    token: string,
    email: string,
    userInfo: { id: number; name: string },
    scope: number | undefined,
    saveAccount: boolean,
  ): Promise<void> {
    if (!this.shouldSaveAccount(saveAccount, scope)) {
      return
    }

    const account: StoredAccount = {
      id: userInfo.id,
      name: userInfo.name,
      email: email,
      token: token,
      lastLoginTime: Date.now(),
    }

    await this.saveAccount(account)
  }
}
